import { computed, ComputedRef, inject, provide, Ref } from "vue";
import { injectCarouselContext } from "../lib/provideInjectors";
import { Dimensions } from "@fresh8/vue-utils/src/dimensionSync";
import { injectAdSize } from "./useAdSize";
import { injectMarketCards } from "../composition/card-creation";
import { injectCardNumber } from "./useAnalytics";

import {
  injectTemplateSize,
  TemplateSizeContext
} from "@fresh8/ad-data-composition/src/useTemplateSize";

export const SQUARISH = "squarish";
export const LANDSCAPE = "landscape";
export const SMALL_RESPONSIVE = "small-responsive";
export const LEADERBOARD = "leaderboard";

export function useLayout(
  dimensions: Ref<Dimensions> = injectAdSize()
): {
  dimensions: Ref<Dimensions>;
  layout: ComputedRef<"small-responsive" | "squarish" | "landscape">;
  SQUARISH: string;
} {
  const carouselContext = injectCarouselContext();

  const layout = computed(() => {
    const { width, height } = dimensions.value;

    if (carouselContext.value.isCarousel) {
      if (height < 200) {
        return SMALL_RESPONSIVE;
      }

      return SQUARISH;
    }

    if (width <= 450) {
      if (height < 200) {
        return SMALL_RESPONSIVE;
      }
      return SQUARISH;
    } else {
      if (height < 200) {
        return SMALL_RESPONSIVE;
      }
      return LANDSCAPE;
    }
  });

  return {
    dimensions,
    layout,
    SQUARISH
  };
}

export type LayoutContext = ReturnType<typeof useLayout>;
export const LayoutSymbol = Symbol("LayoutSymbol");

export function provideLayout(layoutContext: LayoutContext): void {
  provide(LayoutSymbol, layoutContext);
}
export function injectLayout(): LayoutContext {
  const layout = inject<LayoutContext>(LayoutSymbol);

  if (!layout) return {} as LayoutContext;

  return layout;
}

/**
 *
 * hook used to check if is a long mobile leaderboad (980x50) and theres only 1 or 2 cards in card created, and return the correct stylings in cards to center
 */
export function useCenterMobileLeaderboard(): ComputedRef<
  Record<string, string>
> {
  const templateSize = injectTemplateSize() as TemplateSizeContext;
  const { cards } = injectMarketCards();
  const cardNumber = injectCardNumber();

  return computed(() => {
    if (templateSize.size.value.width > 400) {
      if (cardNumber === "1") {
        if (cards.value.length === 1) {
          return {
            width: "100%",
            maxWidth: "325px",
            alignSelf: "center",
            justifySelf: "center"
          };
        }

        if (cards.value.length === 2) {
          return {
            width: "100%",
            maxWidth: "325px",
            alignSelf: "end",
            justifySelf: "center"
          };
        }
      }

      if (cardNumber === "2" && cards.value.length === 2) {
        return {
          width: "100%",
          maxWidth: "325px",
          alignSelf: "start",
          justifySelf: "center"
        };
      }
    }

    return {} as Record<string, string>;
  });
}
